import React from "react";
import { Outlet, NavLink, useNavigate, Link, useLocation } from "react-router-dom";
import {
  Home, Dumbbell, Activity, LineChart, Utensils, Sparkles, User,
  Sun, Moon, LogOut, CalendarDays, Users, Heart, MessageCircle, Menu, Trophy, HeartPulse, Newspaper, UserPlus, Scale, CheckCircle, History, Languages,
} from "lucide-react";
import { Logo } from "@/components/Shared";
import NotificationCenter from "@/components/NotificationCenter";
import { InstallPWA } from "@/components/InstallPWA";
import { useI18n } from "@/context/I18nContext";
import { useTheme } from "@/context/ThemeContext";
import { useAuth } from "@/context/AuthContext";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import api from "@/lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";

const NAV = [
  { to: "/dashboard", icon: Home, key: "home" },
  { to: "/train", icon: Dumbbell, key: "train" },
  { to: "/body", icon: Activity, key: "body" },
  { to: "/progress", icon: LineChart, key: "progress" },
  { to: "/profile", icon: User, key: "profile" },
];

const SECTIONS = [
  { label: "navMain", items: [
    { to: "/dashboard", icon: Home, key: "dashboard" },
    { to: "/checkin", icon: CheckCircle, key: "checkIn" },
    { to: "/history", icon: History, key: "history" },
  ] },
  { label: "navTraining", items: [
    { to: "/train", icon: Dumbbell, key: "train" },
    { to: "/cardio", icon: HeartPulse, key: "cardio" },
    { to: "/progress", icon: LineChart, key: "progress" },
    { to: "/achievements", icon: Trophy, key: "achievements" },
    { to: "/calendar", icon: CalendarDays, key: "calendar" },
  ] },
  { label: "navHealth", items: [
    { to: "/body", icon: Scale, key: "body" },
    { to: "/nutrition", icon: Utensils, key: "nutrition" },
    { to: "/sleep", icon: Moon, key: "sleep" },
  ] },
  { label: "navSocial", items: [
    { to: "/feed", icon: Newspaper, key: "feed" },
    { to: "/friends", icon: UserPlus, key: "friends" },
    { to: "/chat", icon: MessageCircle, key: "chat" },
    { to: "/coach", icon: Sparkles, key: "coach" },
  ] },
  { label: "navAccount", items: [
    { to: "/profile", icon: User, key: "profile" },
  ] },
];

const DONATE_URL = "https://saweria.co/fitbersama";

export default function AppShell() {
  const { t, lang, setLang } = useI18n();
  const { theme, toggleTheme } = useTheme();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [chatUnread, setChatUnread] = React.useState(0);
  const [dmUnread, setDmUnread] = React.useState(0);

  React.useEffect(() => {
    const poll = async () => {
      try { const { data } = await api.get("/dm/unread"); setDmUnread(data.count || 0); } catch { /* transient */ }
    };
    poll();
    const bump = () => poll();
    window.addEventListener("fp-dm-read", bump);
    const id = setInterval(poll, 6000);
    return () => { clearInterval(id); window.removeEventListener("fp-dm-read", bump); };
  }, [location.pathname]);

  React.useEffect(() => {
    if (!localStorage.getItem("fp_chat_read_at")) {
      localStorage.setItem("fp_chat_read_at", new Date().toISOString());
    }
    const poll = async () => {
      if (location.pathname === "/chat") { setChatUnread(0); return; }
      try {
        const after = localStorage.getItem("fp_chat_read_at");
        const { data } = await api.get("/chat/unread", { params: { after } });
        setChatUnread(data.count || 0);
      } catch { /* transient */ }
    };
    const clear = () => setChatUnread(0);
    window.addEventListener("fp-chat-read", clear);
    poll();
    const id = setInterval(poll, 8000);
    return () => { clearInterval(id); window.removeEventListener("fp-chat-read", clear); };
  }, [location.pathname]);

  const doLogout = async () => { await logout(); navigate("/", { replace: true }); };
  const initials = (user?.name || "U").split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase();
  const isTrainer = user?.profile?.trainer_mode;
  const sections = SECTIONS.map((s) => (
    s.label === "navAccount" && isTrainer
      ? { ...s, items: [{ to: "/trainer", icon: Users, key: "trainer" }, ...s.items] }
      : s
  ));
  const [moreOpen, setMoreOpen] = React.useState(false);
  const primaryPaths = ["/dashboard", "/train", "/body", "/progress"];

  return (
    <div className="min-h-screen bg-background">
      {/* Desktop sidebar */}
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-64 flex-col border-r bg-card lg:flex">
        <div className="px-6 py-5"><Logo /></div>
        <nav className="flex-1 space-y-5 overflow-y-auto no-scrollbar px-3 py-2">
          {sections.map((section) => (
            <div key={section.label} className="space-y-1">
              <div className="px-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/60">{t(section.label)}</div>
              {section.items.map((item) => (
                <NavLink key={item.to} to={item.to} data-testid={`side-${item.key}`}
                  className={({ isActive }) => cn(
                    "flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors",
                    isActive ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-accent hover:text-foreground"
                  )}>
                  <item.icon className="h-[18px] w-[18px]" /> <span className="flex-1">{t(item.key)}</span>
                  {item.to === "/chat" && dmUnread > 0 && (
                    <span className="grid h-5 min-w-5 place-items-center rounded-full bg-red-500 px-1.5 text-[10px] font-bold text-white tabular-nums" data-testid="side-dm-badge">{dmUnread > 9 ? "9+" : dmUnread}</span>
                  )}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>
        <div className="border-t p-3 space-y-2">
          <a href={DONATE_URL} target="_blank" rel="noopener noreferrer" data-testid="side-donate"
            className="flex items-center gap-3 rounded-xl bg-rose-500/10 px-3 py-2.5 text-sm font-semibold text-rose-500 transition-colors hover:bg-rose-500/20">
            <Heart className="h-[18px] w-[18px]" /> {t("supportServer")}
          </a>
          <DropdownMenu>
            <DropdownMenuTrigger className="flex w-full items-center gap-3 rounded-xl p-2 text-left transition-colors hover:bg-accent" data-testid="user-menu">
              <Avatar className="h-9 w-9">
                <AvatarImage src={user?.picture} />
                <AvatarFallback className="bg-primary/15 text-primary text-xs font-semibold">{initials}</AvatarFallback>
              </Avatar>
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium">{user?.name}</div>
                <div className="truncate text-xs text-muted-foreground">{user?.email}</div>
              </div>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              <DropdownMenuItem onClick={() => navigate("/profile")}>{t("settings")}</DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={doLogout} data-testid="logout-btn" className="text-destructive">
                <LogOut className="mr-2 h-4 w-4" /> {t("logout")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Top bar */}
      <div className="lg:pl-64">
        <header className="sticky top-0 z-30 glass border-b pt-[env(safe-area-inset-top)]">
          <div className="flex items-center justify-between px-5 py-3">
            <div className="lg:hidden"><Logo /></div>
            <div className="hidden lg:block" />
            <div className="flex items-center gap-2">
              <InstallPWA />
              <button onClick={() => setLang(lang === "id" ? "en" : "id")}
                className="hidden rounded-lg border px-2.5 py-1.5 text-xs font-semibold transition-colors hover:bg-accent lg:inline-flex" data-testid="app-lang-toggle">
                {lang === "id" ? "EN" : "ID"}
              </button>
              <button onClick={toggleTheme} className="hidden h-9 w-9 place-items-center rounded-lg border transition-colors hover:bg-accent lg:grid" data-testid="theme-toggle">
                {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
              </button>
              <Link to="/chat" className="relative grid h-9 w-9 place-items-center rounded-lg border transition-colors hover:bg-accent" data-testid="header-chat-btn" aria-label="Chat">
                <MessageCircle className="h-4 w-4" />
                {chatUnread > 0 && (
                  <span className="absolute -right-1 -top-1 grid h-4 min-w-4 place-items-center rounded-full bg-primary px-1 text-[10px] font-bold text-primary-foreground" data-testid="chat-unread-badge">
                    {chatUnread > 9 ? "9+" : chatUnread}
                  </span>
                )}
              </Link>
              <NotificationCenter />
            </div>
          </div>
        </header>

        <main className="mx-auto max-w-6xl px-4 py-6 pb-[calc(7rem_+_env(safe-area-inset-bottom))] lg:px-8 lg:pb-10 fade-in">
          <Outlet />
        </main>
      </div>

      {/* Mobile bottom nav */}
      <nav className="fixed inset-x-0 bottom-0 z-40 glass border-t pb-[env(safe-area-inset-bottom)] lg:hidden">
        <div className="grid grid-cols-5">
          {NAV.slice(0, 4).map((item) => (
            <NavLink key={item.to} to={item.to} data-testid={`bottom-${item.key}`}
              className={({ isActive }) => cn(
                "flex flex-col items-center gap-1 py-2.5 text-[11px] font-medium transition-colors",
                isActive ? "text-primary" : "text-muted-foreground"
              )}>
              <item.icon className="h-5 w-5" /> {t(item.key)}
            </NavLink>
          ))}
          <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
            <SheetTrigger asChild>
              <button data-testid="bottom-more" className="relative flex flex-col items-center gap-1 py-2.5 text-[11px] font-medium text-muted-foreground">
                <Menu className="h-5 w-5" /> {t("more")}
                {(chatUnread > 0 || dmUnread > 0) && <span className="absolute right-6 top-1.5 h-2 w-2 rounded-full bg-primary" />}
              </button>
            </SheetTrigger>
            <SheetContent side="bottom" className="rounded-t-2xl pb-8 max-h-[80vh] overflow-y-auto no-scrollbar" data-testid="more-sheet">
              <SheetHeader className="mb-3 text-left"><SheetTitle>{t("menu")}</SheetTitle></SheetHeader>
              <div className="space-y-5">
                {sections.map((section) => {
                  const items = section.items.filter((i) => !primaryPaths.includes(i.to));
                  if (!items.length) return null;
                  return (
                    <div key={section.label}>
                      <div className="mb-2 px-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/60">{t(section.label)}</div>
                      <div className="grid grid-cols-3 gap-2">
                        {items.map((item) => (
                          <NavLink key={item.to} to={item.to} onClick={() => setMoreOpen(false)} data-testid={`more-${item.key}`}
                            className={({ isActive }) => cn(
                              "relative flex flex-col items-center gap-2 rounded-2xl border p-4 text-xs font-medium transition-colors",
                              isActive ? "border-primary/50 bg-primary/10 text-primary" : "text-foreground hover:bg-accent"
                            )}>
                            <item.icon className="h-6 w-6" /> {t(item.key)}
                            {item.to === "/chat" && chatUnread > 0 && (
                              <span className="absolute right-2 top-2 grid h-4 min-w-4 place-items-center rounded-full bg-primary px-1 text-[10px] font-bold text-primary-foreground">{chatUnread > 9 ? "9+" : chatUnread}</span>
                            )}
                            {item.to === "/chat" && dmUnread > 0 && (
                              <span className="absolute left-2 top-2 grid h-4 min-w-4 place-items-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white" data-testid="more-dm-badge">{dmUnread > 9 ? "9+" : dmUnread}</span>
                            )}
                          </NavLink>
                        ))}
                      </div>
                    </div>
                  );
                })}
                <div>
                  <div className="mb-2 px-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/60">{t("settings")}</div>
                  <div className="grid grid-cols-2 gap-2">
                    <button onClick={() => setLang(lang === "id" ? "en" : "id")} data-testid="more-lang-toggle"
                      className="flex h-12 items-center justify-center gap-2 rounded-2xl border text-sm font-medium transition-colors hover:bg-accent">
                      <Languages className="h-5 w-5" /> {lang === "id" ? "English" : "Bahasa Indonesia"}
                    </button>
                    <button onClick={toggleTheme} data-testid="more-theme-toggle"
                      className="flex h-12 items-center justify-center gap-2 rounded-2xl border text-sm font-medium transition-colors hover:bg-accent">
                      {theme === "dark" ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
                      {theme === "dark" ? (lang === "id" ? "Mode Terang" : "Light mode") : (lang === "id" ? "Mode Gelap" : "Dark mode")}
                    </button>
                  </div>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </nav>
    </div>
  );
}
